/* eslint-disable @typescript-eslint/no-explicit-any */
import { faLightbulb } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Breadcrumbs,
  Button,
  Content,
  Loading,
  Popover,
  PopoverContent,
} from '@orobix/orobix-fe-components'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import { useDispatch, useStore } from '../../store/store'
import {
  fetchProduct,
  fetchProject,
  fetchTraining,
  fetchTrainingTest,
} from '../../store/storeActions'
import { locations } from '../../utils/locations'
import { ScoresHistogram } from './ScoresHistogram'
import useStyles from './TrainingTestRoot.styles'
import { useFetchAllTestOutcomes } from './useFetchAllTestOutcomes'

interface RouteParams {
  projectId: string
  productId: string
  trainingId: string
  trainingTestId: string
}

const AnomalyScorePlot = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const { product, project, training, trainingTest } = useStore()
  const dispatch = useDispatch()

  const {
    productId,
    projectId,
    trainingId,
    trainingTestId,
  } = useParams<RouteParams>()

  function getDataOfSchemaType(schematype: string, responseAsJSONObject: any) {
    let data = []
    try {
      data = responseAsJSONObject.filter(
        (o: any) => o.schemaType === schematype
      )[0].data
      // eslint-disable-next-line no-empty
    } catch (e) {}
    return data
  }

  const { testOutcomes } = useFetchAllTestOutcomes(Number(trainingTestId))

  const threshold =
    (trainingTest &&
      getDataOfSchemaType('anomaly_score_histogram', testOutcomes).threshold) ||
    0

  const anomalyScores =
    (testOutcomes &&
      getDataOfSchemaType('anomaly_score_histogram', testOutcomes)
        .anomalyScores) ||
    []

  const normalScores =
    (testOutcomes &&
      getDataOfSchemaType('anomaly_score_histogram', testOutcomes)
        .normalScores) ||
    []

  const showHistogram =
    threshold && (anomalyScores.length !== 0 || normalScores.length !== 0)

  useEffect(() => {
    if (!project) {
      fetchProject(dispatch, Number(projectId))
    }
  }, [dispatch, project, projectId])

  useEffect(() => {
    if (!product) {
      fetchProduct(dispatch, Number(productId))
    }
  }, [dispatch, product, productId])

  useEffect(() => {
    if (!training) {
      fetchTraining(dispatch, Number(trainingId))
    }
  }, [dispatch, training, trainingId])

  useEffect(() => {
    if (!trainingTest) {
      fetchTrainingTest(dispatch, Number(trainingTestId))
    }
  }, [dispatch, trainingTest, trainingTestId])

  function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window
    return {
      width,
      height,
    }
  }

  function useWindowDimensions() {
    const [windowDimensions, setWindowDimensions] = useState(
      getWindowDimensions()
    )

    useEffect(() => {
      function handleResize() {
        setWindowDimensions(getWindowDimensions())
      }

      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [])

    return windowDimensions
  }

  const { height, width } = useWindowDimensions()

  return product && project && training && trainingTest ? (
    <Content
      childrenClassName={classes.content}
      breadcrumbs={
        <Breadcrumbs
          elements={[
            <Link to={locations.projects}>{t('Projects')}</Link>,
            <Link to={locations.products(Number(projectId))}>
              {project.name}
            </Link>,
            <Link
              to={locations.trainings(Number(projectId), Number(productId))}
            >
              {product.name}
            </Link>,
            <Link
              to={locations.trainings(
                Number(projectId),
                Number(productId),
                Number(trainingId)
              )}
            >
              {training.name}
            </Link>,
            trainingTest.name,
          ]}
        />
      }
    >
      <div className={classes.trainingTest}>
        {showHistogram && (
          <div className={classes.chartContainer}>
            <div className={classes.histogramHeader}>
              <p className={classes.chartHeader}>
                {t('Cumulative plot of anomaly score')}
              </p>
              <Popover
                renderContent={() => (
                  <PopoverContent width="max-content">
                    <ul
                      className={classes.paddingLeft} // fix user agent stylesheet
                    >
                      <li>{t('Green line: normal')}</li>
                      <li>{t('Red line: anomaly')}</li>
                      <li>{t('Dotted line: threshold')}</li>
                    </ul>
                  </PopoverContent>
                )}
              >
                <Button
                  label={t('Tips')}
                  title={t('Tips')}
                  hideLabel
                  icon={<FontAwesomeIcon icon={faLightbulb} />}
                  type="blank-default"
                  buttonType="button"
                />
              </Popover>
            </div>
            <ScoresHistogram
              anomalyScores={anomalyScores}
              normalScores={normalScores}
              thresholdX={threshold}
              svgWid={width}
              svgHei={height - 180}
            />
          </div>
        )}
      </div>
    </Content>
  ) : (
    <Content>
      <Loading />
    </Content>
  )
}

export default AnomalyScorePlot
